import React from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import { motion, useReducedMotion } from 'motion/react';
import { content, SkillItem } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useParallax } from '../../hooks/useParallax';

const MotionBox = motion.create(Box);

const SkillPill: React.FC<{ skill: SkillItem }> = ({ skill }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1,
        px: 2, py: 0.875, borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper', cursor: 'default',
        whiteSpace: 'nowrap', flexShrink: 0,
        transition: 'border-color 220ms ease, background-color 220ms ease, box-shadow 220ms ease',
        '&:hover': {
          borderColor: skill.color,
          bgcolor: `${skill.color}0F`,
          boxShadow: `0 0 14px ${skill.color}30`,
        },
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: skill.color, flexShrink: 0, opacity: 0.9, boxShadow: `0 0 6px ${skill.color}80` }} />
      <Typography component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
        {skill.name}
      </Typography>
    </Box>
  );
};

// Defined here (not in index.css) so the rows animate without loading other global styles.
// The track holds 4 copies and moves by half its width (2 copies), so the loop is seamless.
const scrollRightToLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;
const scrollLeftToRight = keyframes`
  from { transform: translateX(-50%); }
  to   { transform: translateX(0); }
`;

const MarqueeRow: React.FC<{ items: SkillItem[]; reverse?: boolean }> = ({ items, reverse = false }) => {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const doubled = [...items, ...items, ...items, ...items];
  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 120, zIndex: 1, background: `linear-gradient(to right, ${theme.palette.background.default}, transparent)`, pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 120, zIndex: 1, background: `linear-gradient(to left, ${theme.palette.background.default}, transparent)`, pointerEvents: 'none' }} />
      <Box sx={{ display: 'flex', gap: 1.5, width: 'max-content', animation: reduceMotion ? 'none' : `${reverse ? scrollLeftToRight : scrollRightToLeft} 40s linear infinite`, '&:hover': { animationPlayState: 'paused' }, py: 0.5 }}>
        {doubled.map((skill, i) => <SkillPill key={`${skill.name}-${i}`} skill={skill} />)}
      </Box>
    </Box>
  );
};

const Skills: React.FC = () => {
  const theme = useTheme();
  const { overline, title, categories } = content.skills;
  const { fadeUp, stagger } = useAnimationVariants();
  const headerParallax = useParallax({ outputRange: [40, -40] });

  return (
    <Box id="skills" sx={{ py: { xs: 10, md: 14 }, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', bottom: '-15%', left: '-8%', width: '38%', paddingBottom: '38%', borderRadius: '50%', background: `radial-gradient(circle, ${theme.palette.primary.main}0C 0%, transparent 70%)`, pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header with parallax */}
        <Box ref={headerParallax.ref}>
          <MotionBox style={{ y: headerParallax.y }}>
            <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} sx={{ textAlign: 'center', mb: { xs: 6, md: 9 } }}>
              <MotionBox variants={fadeUp}>
                <Typography variant="overline" sx={{ color: 'primary.main', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.14em', mb: 1.5, display: 'block' }}>
                  {overline}
                </Typography>
              </MotionBox>
              <MotionBox variants={fadeUp}>
                <Typography variant="h2" component="h2" sx={{ color: 'text.primary' }}>
                  {title}
                </Typography>
              </MotionBox>
            </MotionBox>
          </MotionBox>
        </Box>

        {/* Marquee rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 7 } }}>
          {categories.map((category, catIndex) => (
            <MotionBox
              key={category.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, delay: catIndex * 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Category label with index number */}
              <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5625rem', color: 'primary.main', opacity: 0.5, letterSpacing: '0.08em', flexShrink: 0 }}>
                  {String(catIndex + 1).padStart(2, '0')}
                </Typography>
                {/* Animated gradient line */}
                <MotionBox
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: catIndex * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  sx={{ width: 24, height: 2, background: `linear-gradient(90deg, ${theme.palette.primary.main}, #a78bfa)`, borderRadius: 1, transformOrigin: 'left', flexShrink: 0 }}
                />
                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.1em' }}>
                  {category.label}
                </Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', borderRadius: 1 }} />
              </Box>
              {/* Rows 1, 3, 5 move right-to-left; rows 2, 4, 6 move left-to-right */}
              <MarqueeRow items={category.items} reverse={catIndex % 2 === 1} />
            </MotionBox>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Skills;
